/**
 * Core API Types
 * Tüm API katmanında kullanılan temel tipler
 */

// ==================== BASE TYPES ====================

import { AlarmSeverity, AlarmStatus } from './requests/common.types';

export type ID = string | number;

export interface BaseQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  filters?: Record<string, string | number | boolean | null>;
  lat?: number;
  lng?: number;
  radius?: number;
  unit?: 'km' | 'miles';
}

export interface DateRangeFilter {
  startDate?: string | Date;
  endDate?: string | Date;
}

export type ExportFormat = 'csv' | 'excel' | 'pdf' | 'json';

// ==================== API RESPONSE TYPES ====================

export interface ApiResponse<T = object> {
  data: T;
  message: string;
  status: number;
  success: boolean;
  timestamp: string;
  path?: string;
  requestId?: string;
  meta?: PaginationMeta;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  meta: PaginationMeta;
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  sortBy: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ApiErrorResponse {
  message: string;
  status: number;
  error: string;
  path?: string;
  timestamp: string;
  validationErrors?: ValidationError[];
  code?: string;
  requestId?: string;
}

export interface ValidationError {
  field: string;
  message: string;
  code?: string;
  value?: string | number | boolean;
}

// ==================== GENERIC OPERATION TYPES ====================

export interface CreateRequest<T> {
  data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>;
}

export interface UpdateRequest<T> {
  id: ID;
  data: Partial<Omit<T, 'id' | 'createdAt' | 'updatedAt'>>;
}

export interface DeleteRequest {
  id: ID;
  hardDelete?: boolean;
  reason?: string;
}

export interface BulkOperation {
  ids: ID[];
  operation: 'delete' | 'update' | 'export' | 'acknowledge' | 'resolve';
  data?: object;
}

// ==================== AUTH TYPES ====================

export type UserRole = 'superadmin' | 'admin' | 'manager' | 'operator' | 'viewer';

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatar?: string;
  phone?: string;
  role: UserRole;
  permissions: string[];
  tenantId?: string;
  isActive: boolean;
  isVerified: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== ALARM TYPES ====================



export interface AlarmType {
  id: string;
  code: string;
  name: string;
  description?: string;
  severity: AlarmSeverity;
  category: string;
}

export interface Alarm {
  id: string;
  alarmId: string;
  type: AlarmType;
  unitId: string;
  unitName?: string;
  wagonId?: string;
  wagonName?: string;
  bogieId?: string;
  wheelId?: string;
  severity: AlarmSeverity;
  status: AlarmStatus;
  message: string;
  description?: string;
  timestamp: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  data?: object;
  metadata?: object;
}

export interface AlarmStatistics {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  active: number;
  acknowledged: number;
  resolved: number;
  falseAlarms: number;
  byUnit: Array<{ unitId: string; unitName: string; count: number }>;
  byType: Array<{ typeId: string; typeName: string; count: number }>;
  trend: Array<{ date: string; count: number }>;
}

// ==================== UNIT TYPES ====================

export type UnitType = 'train' | 'locomotive' | 'wagon' | 'equipment';

export type UnitStatus = 'operational' | 'maintenance' | 'out_of_service' | 'retired';

export interface Unit {
  id: string;
  unitId: string;
  name: string;
  description?: string;
  type: UnitType;
  status: UnitStatus;
  fleetId?: string;
  fleetName?: string;
  tenantId: string;
  tenantName?: string;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  commissionDate?: string;
  lastMaintenance?: string;
  nextMaintenance?: string;
  location?: {
    lat: number;
    lng: number;
    address?: string;
  };
  specifications?: object;
  metadata?: object;
  createdAt: string;
  updatedAt: string;
}

export interface UnitPerformance {
  unitId: string;
  availability: number;
  reliability: number;
  efficiency: number;
  mtbf: number;
  mttr: number;
  uptime: number;
  downtime: number;
  alarmCount: number;
  lastAlarm?: string;
  score: number;
  trend: 'improving' | 'stable' | 'declining';
}

// ==================== FLEET TYPES ====================

export interface Fleet {
  id: string;
  fleetId: string;
  name: string;
  description?: string;
  tenantId: string;
  tenantName?: string;
  managerId?: string;
  managerName?: string;
  unitCount: number;
  status: 'active' | 'inactive' | 'archived';
  metadata?: object;
  createdAt: string;
  updatedAt: string;
}

// ==================== WAGON TYPES ====================

export interface Wagon {
  id: string;
  wagonId: string;
  name: string;
  description?: string;
  type: string;
  unitId: string;
  unitName?: string;
  position: number;
  status: 'operational' | 'maintenance' | 'out_of_service';
  specifications?: object;
  metadata?: object;
  createdAt: string;
  updatedAt: string;
}

// ==================== TENANT TYPES ====================

export interface Tenant {
  id: string;
  name: string;
  description?: string;
  domain?: string;
  logo?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  isActive: boolean;
  settings?: object;
  metadata?: object;
  createdAt: string;
  updatedAt: string;
}

// ==================== BUILDING TYPES ====================

export interface Building {
  id: string;
  buildingId: string;
  name: string;
  description?: string;
  address: string;
  location: {
    lat: number;
    lng: number;
  };
  tenantId: string;
  tenantName?: string;
  unitCount: number;
  status: 'active' | 'inactive' | 'under_maintenance';
  metadata?: object;
  createdAt: string;
  updatedAt: string;
}

// ==================== NOTIFICATION TYPES ====================

export interface Notification {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  data?: object;
  read: boolean;
  userId: string;
  createdAt: string;
  readAt?: string;
}

// ==================== UTILITY TYPES ====================

export type DeepPartial<T> = T extends object
  ? { [P in keyof T]?: DeepPartial<T[P]> }
  : T;

export type DeepRequired<T> = T extends object
  ? { [P in keyof T]-?: DeepRequired<T[P]> }
  : T;

export type Nullable<T> = { [P in keyof T]: T[P] | null };

export type AsyncFunction<T = object> = (...args: unknown[]) => Promise<T>;

// ==================== TYPE GUARDS ====================

export function isApiResponse<T>(obj: object): obj is ApiResponse<T> {
  const test = obj as ApiResponse<T>;
  return (
    'data' in test &&
    'message' in test &&
    typeof test.message === 'string' &&
    'status' in test &&
    typeof test.status === 'number' &&
    'success' in test &&
    typeof test.success === 'boolean' &&
    'timestamp' in test &&
    typeof test.timestamp === 'string'
  );
}

export function isPaginatedResponse<T>(obj: object): obj is PaginatedResponse<T> {
  if (!isApiResponse<T[]>(obj)) return false;
  const test = obj as PaginatedResponse<T>;
  return 'meta' in test && typeof test.meta === 'object' && test.meta !== null;
}

export function isApiErrorResponse(obj: object): obj is ApiErrorResponse {
  const test = obj as ApiErrorResponse;
  return (
    'message' in test &&
    typeof test.message === 'string' &&
    'status' in test &&
    typeof test.status === 'number' &&
    'error' in test &&
    typeof test.error === 'string' &&
    'timestamp' in test &&
    typeof test.timestamp === 'string'
  );
}

export function isValidationError(obj: object): obj is ValidationError {
  const test = obj as ValidationError;
  return (
    'field' in test &&
    typeof test.field === 'string' &&
    'message' in test &&
    typeof test.message === 'string'
  );
}

// ==================== HELPER TYPES ====================

export interface ApiEndpoint {
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  requiresAuth?: boolean;
  description?: string;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean>;
  timeout?: number;
  retryCount?: number;
  cacheKey?: string;
  cacheTTL?: number;
  skipAuthRefresh?: boolean;
}

export type ResponseInterceptor = (response: object) => object | Promise<object>;

export type ErrorInterceptor = (error: Error) => Error | Promise<Error>;

// ==================== EVENT TYPES ====================

export type ApiEvent =
  | 'request:start'
  | 'request:end'
  | 'request:error'
  | 'auth:login'
  | 'auth:logout'
  | 'auth:token-expired'
  | 'network:online'
  | 'network:offline'
  | 'cache:clear'
  | 'cache:hit'
  | 'cache:miss';

export type ApiEventListener = (event: ApiEvent, data?: object) => void;

// ==================== EXPORT ====================

const apiTypes = {
  isApiResponse,
  isPaginatedResponse,
  isApiErrorResponse,
  isValidationError
};

export default apiTypes;